/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CUSTOMER_SERVICE_BASE_URL, CUSTOMER_SERVICE_UNREAD_ENDPOINT } from '../config/appConfig';
import { fetchClientRuntimeConfig } from '../services/api';
import type { ClientRuntimeConfig } from '../types';
import { reportClientError } from '../utils/error';

type AppConfigContextValue = {
  appConfig: ClientRuntimeConfig;
  loading: boolean;
  refresh: () => Promise<void>;
};

const defaultConfig: ClientRuntimeConfig = {
  productName: '享分期',
  customerServiceBaseUrl: CUSTOMER_SERVICE_BASE_URL,
  customerServiceUnreadEndpoint: CUSTOMER_SERVICE_UNREAD_ENDPOINT,
  customerServiceToken: '',
  customerServiceUnreadCountEnabled: false,
};

const normalizeProductName = (value: unknown) =>
  String(value || defaultConfig.productName).trim() || defaultConfig.productName;

const AppConfigContext = createContext<AppConfigContextValue>({
  appConfig: defaultConfig,
  loading: false,
  refresh: async () => {},
});

export const AppConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [appConfig, setAppConfig] = useState<ClientRuntimeConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const refreshSeqRef = useRef(0);

  const refresh = useCallback(async () => {
    const seq = refreshSeqRef.current + 1;
    refreshSeqRef.current = seq;
    setLoading(true);
    try {
      const result = await fetchClientRuntimeConfig();
      if (seq !== refreshSeqRef.current) return;
      setAppConfig({
        productName: normalizeProductName(result.productName),
        customerServiceBaseUrl:
          String(result.customerServiceBaseUrl || defaultConfig.customerServiceBaseUrl).trim() ||
          defaultConfig.customerServiceBaseUrl,
        customerServiceUnreadEndpoint:
          String(
            result.customerServiceUnreadEndpoint || defaultConfig.customerServiceUnreadEndpoint
          ).trim() || defaultConfig.customerServiceUnreadEndpoint,
        customerServiceToken:
          String(result.customerServiceToken || defaultConfig.customerServiceToken).trim() ||
          defaultConfig.customerServiceToken,
        customerServiceUnreadCountEnabled: Boolean(
          result.customerServiceUnreadCountEnabled ?? defaultConfig.customerServiceUnreadCountEnabled
        ),
      });
    } catch (error) {
      if (seq !== refreshSeqRef.current) return;
      reportClientError('app_config.load', error);
      setAppConfig(defaultConfig);
    } finally {
      if (seq !== refreshSeqRef.current) return;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = normalizeProductName(appConfig.productName);
    }
  }, [appConfig.productName]);

  const value = useMemo(
    () => ({
      appConfig,
      loading,
      refresh,
    }),
    [appConfig, loading, refresh]
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = () => useContext(AppConfigContext);
